// Face detection for the house wall-graph (Stage 9b).
//
// A floor plan is a planar graph (see graph.js): vertices + undirected walls,
// already normalised so no two walls overlap collinearly and T-junctions are
// split. The enclosed minimal cycles of that graph are the ROOMS; the single
// unbounded cycle that wraps each connected component is its OUTER face — the
// house footprint.
//
// Algorithm — planar face traversal via half-edges (darts):
//   * Every undirected wall becomes two directed darts (a→b and b→a).
//   * Around each vertex the outgoing darts are sorted by angle (CCW).
//   * To walk a face, the dart following (u→v) is the CLOCKWISE neighbour of
//     the twin (v→u) in v's angular order. Following `next` until we return to
//     the start traces exactly one face, and every dart belongs to one face.
//   * With this fixed rule every BOUNDED face has the same signed-area sign and
//     every UNBOUNDED (outer) face has the opposite sign — so we classify by
//     sign, which is robust to disconnected components (each contributes its
//     own outer face) and to the single-room case (where outer and room have
//     equal |area| and a magnitude test would be ambiguous).
//
// Pure JS, no DOM — unit-tested with Vitest. Inputs are never mutated.

// Signed area of bounded faces under our (y-down) shoelace + the clockwise
// `next` rule below. Pinned by the "internal wall splits into two rooms" test
// (the single-rectangle case is sign-ambiguous since room and outer have equal
// |area|); if the traversal handedness ever changes, that test fails loudly.
// Bounded faces come out POSITIVE, outer faces NEGATIVE.
const BOUNDED_SIGN = 1

const AREA_EPS = 1e-6

// Shoelace signed area in the local (x east, y south) frame.
export function signedAreaM2(polygon) {
  let s = 0
  const n = polygon.length
  for (let i = 0; i < n; i++) {
    const [x1, y1] = polygon[i]
    const [x2, y2] = polygon[(i + 1) % n]
    s += x1 * y2 - x2 * y1
  }
  return s / 2
}

export function polygonAreaM2(polygon) {
  return Math.abs(signedAreaM2(polygon))
}

export function polygonCentroid(polygon) {
  // Area-weighted centroid (correct for concave/L shapes, unlike the vertex
  // average). Falls back to the vertex average for degenerate (zero-area) rings.
  const n = polygon.length
  let a = 0, cx = 0, cy = 0
  for (let i = 0; i < n; i++) {
    const [x1, y1] = polygon[i]
    const [x2, y2] = polygon[(i + 1) % n]
    const cross = x1 * y2 - x2 * y1
    a += cross
    cx += (x1 + x2) * cross
    cy += (y1 + y2) * cross
  }
  a /= 2
  if (Math.abs(a) < AREA_EPS) {
    let sx = 0, sy = 0
    for (const [x, y] of polygon) { sx += x; sy += y }
    return [sx / n, sy / n]
  }
  return [cx / (6 * a), cy / (6 * a)]
}

// Trace every face of the graph. Returns [{ loop:[vertexId...],
// polygon:[[x,y]...], signedArea, area }]. Degenerate (zero-area) faces — e.g.
// the in-and-back walk along a dangling wall — are dropped.
export function detectFaces(graph) {
  const verts = new Map((graph.vertices ?? []).map((v) => [v.id, v]))

  // Unique undirected walls (defensive against any accidental parallel edge —
  // a duplicate dart would corrupt the angular order and the visited set).
  const pairSeen = new Set()
  const darts = []
  for (const w of graph.walls ?? []) {
    if (w.a === w.b) continue
    if (!verts.has(w.a) || !verts.has(w.b)) continue
    const key = w.a < w.b ? `${w.a}|${w.b}` : `${w.b}|${w.a}`
    if (pairSeen.has(key)) continue
    pairSeen.add(key)
    darts.push([w.a, w.b], [w.b, w.a])
  }
  if (!darts.length) return []

  const angleOf = (from, to) => {
    const a = verts.get(from)
    const b = verts.get(to)
    return Math.atan2(b.yM - a.yM, b.xM - a.xM)
  }

  // out: vertexId → darts leaving it, sorted CCW by angle.
  const out = new Map()
  for (const [u, v] of darts) {
    if (!out.has(u)) out.set(u, [])
    out.get(u).push({ to: v, angle: angleOf(u, v) })
  }
  for (const arr of out.values()) arr.sort((p, q) => p.angle - q.angle)
  const indexAt = (v, target) => out.get(v).findIndex((d) => d.to === target)

  const dartKey = (u, v) => `${u}>${v}`
  const visited = new Set()
  const faces = []

  for (const [u0, v0] of darts) {
    if (visited.has(dartKey(u0, v0))) continue
    const loop = []
    let u = u0
    let v = v0
    let guard = 0
    while (guard++ <= darts.length) {
      visited.add(dartKey(u, v))
      loop.push(u)
      // next dart: clockwise neighbour of the twin (v→u) around v.
      const arr = out.get(v)
      const twinIdx = indexAt(v, u)
      const nextIdx = (twinIdx - 1 + arr.length) % arr.length
      u = v
      v = arr[nextIdx].to
      if (u === u0 && v === v0) break
    }
    const polygon = loop.map((id) => {
      const p = verts.get(id)
      return [p.xM, p.yM]
    })
    const signedArea = signedAreaM2(polygon)
    if (Math.abs(signedArea) < AREA_EPS) continue
    faces.push({ loop, polygon, signedArea, area: Math.abs(signedArea) })
  }
  return faces
}

// Normalise a ring to positive signed area (a stable, winding-independent form
// for storage and rendering). Returns a new array.
function toPositiveWinding(polygon) {
  return signedAreaM2(polygon) < 0 ? [...polygon].reverse() : polygon
}

// Detect rooms: the bounded faces. Returns
// [{ polygon:[[x,y]...], areaM2, centroid:[x,y] }] with positive winding,
// sorted largest-first for stable ordering.
export function detectRooms(graph) {
  const rooms = detectFaces(graph)
    .filter((f) => Math.sign(f.signedArea) === BOUNDED_SIGN)
    .map((f) => {
      const polygon = toPositiveWinding(f.polygon)
      return { polygon, areaM2: f.area, centroid: polygonCentroid(polygon) }
    })
  rooms.sort((a, b) => b.areaM2 - a.areaM2)
  return rooms
}

// Ray-cast point-in-polygon (local copy — keeps faces.js dependency-free).
function pointInPolygon(x, y, polygon) {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1]
    const xj = polygon[j][0], yj = polygon[j][1]
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

// Re-detect rooms from the current graph and carry the user's name/type across
// the edit. A previous room is matched to the detected face that now contains
// its centroid (nearest centroid wins ties); unmatched faces become fresh rooms
// with the default type. Returns persisted-shape rooms:
//   [{ name, type, polygon, areaM2, centroid }]
// `defaultType` lets the caller decide the type for brand-new faces.
export function reconcileRooms(graph, prevRooms = [], defaultType = "other") {
  const detected = detectRooms(graph)
  const prev = prevRooms.map((r) => ({ ...r, _used: false }))
  return detected.map((face) => {
    let best = null
    let bestD = Infinity
    for (const p of prev) {
      if (p._used || !Array.isArray(p.centroid)) continue
      if (!pointInPolygon(p.centroid[0], p.centroid[1], face.polygon)) continue
      const d = Math.hypot(p.centroid[0] - face.centroid[0], p.centroid[1] - face.centroid[1])
      if (d < bestD) { bestD = d; best = p }
    }
    if (best) best._used = true
    return {
      name: best?.name ?? "",
      type: best?.type ?? defaultType,
      polygon: face.polygon,
      areaM2: face.areaM2,
      centroid: face.centroid,
    }
  })
}

// Classify every wall as "exterior" or "interior" (Stage 14a). A wall is
// INTERIOR when it is shared by two rooms — i.e. BOTH of its directed darts
// (a→b and b→a) bound a face that is a room (bounded face). A wall is EXTERIOR
// when at least one side faces the outside (one dart lies on the unbounded outer
// face), which also covers dangling walls (both darts on the outer face).
// Returns Map<wallId, "exterior"|"interior">. Robust to disconnected components.
export function classifyWalls(graph) {
  const boundedDarts = new Set()
  for (const f of detectFaces(graph)) {
    if (Math.sign(f.signedArea) !== BOUNDED_SIGN) continue
    const loop = f.loop
    for (let i = 0; i < loop.length; i++) {
      boundedDarts.add(`${loop[i]}>${loop[(i + 1) % loop.length]}`)
    }
  }
  const kinds = new Map()
  for (const w of graph.walls ?? []) {
    const shared = boundedDarts.has(`${w.a}>${w.b}`) && boundedDarts.has(`${w.b}>${w.a}`)
    kinds.set(w.id, shared ? "interior" : "exterior")
  }
  return kinds
}

// The house footprint: the outer boundary of the largest connected component.
// Returns a positive-winding polygon [[x,y]...] or null for an empty graph.
// (For a multi-component graph there are several outer faces; we return the
// biggest, which is the dominant building — multi-blob houses are not a
// supported plan shape yet. TODO(stage-9d): multi-building plans need every
// outer face returned so the norms validator enforces clearance around each.)
export function outerContour(graph) {
  const outers = detectFaces(graph).filter(
    (f) => Math.sign(f.signedArea) === -BOUNDED_SIGN,
  )
  if (!outers.length) return null
  outers.sort((a, b) => b.area - a.area)
  return toPositiveWinding(outers[0].polygon)
}
